import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/Brand";
import { Reveal } from "@/lib/motion";
import { SITE } from "@/lib/site";

const SECTIONS = [
  {
    title: "Information we collect",
    body: [
      "When you submit an enquiry through this website, we may collect your name, phone number, email address, installation address, suburb, postcode, property details, information about your existing heating or cooling equipment, and the contents of your message.",
      "Where you upload photographs or documents (for example, as part of a warranty or rebate enquiry), those files and the information they contain are also collected.",
    ],
  },
  {
    title: "How we use your information",
    body: [
      "We use the information you provide to respond to your enquiry, prepare quotations, assess potential rebate eligibility, manage warranty enquiries, schedule installations and provide after-sales support.",
      "Enquiry details are stored in our enquiry management system so our team can track and respond to your request.",
    ],
  },
  {
    title: "Phone and email enquiries",
    body: [
      "If you contact us by phone or email, we collect the information you choose to provide during that correspondence for the purpose of responding to and managing your enquiry.",
    ],
  },
  {
    title: "Analytics and cookies",
    body: [
      "This website may use analytics tools (such as Google Analytics) and cookies to understand how visitors use the site and to improve its performance. Analytics data is generally aggregated and does not directly identify you.",
      "You can control cookies through your browser settings.",
    ],
  },
  {
    title: "Third-party service providers",
    body: [
      "We may use trusted third-party service providers to operate this website, deliver enquiry notification emails, host data and provide analytics. These providers handle information only as needed to provide their services.",
      "Where a Victorian Energy Upgrades incentive applies, relevant installation and eligibility information may be shared with an independent participating VEU Accredited Provider for the purpose of processing that incentive.",
    ],
  },
  {
    title: "Information security",
    body: [
      "We take reasonable steps to protect the personal information we hold from misuse, interference, loss, and unauthorised access, modification or disclosure. However, no method of transmission over the internet is completely secure.",
    ],
  },
  {
    title: "Access and correction",
    body: [
      "You may request access to, or correction of, the personal information we hold about you by contacting us using the details below.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "We may update this privacy policy from time to time. The current version will always be published on this page.",
    ],
  },
];

export default function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy | Airconditioning Victoria"
        description="How Airconditioning Victoria collects, uses and protects personal information submitted through this website."
        path="/privacy-policy"
      />
      <section className="bg-white py-16 md:py-24" data-testid="privacy-page">
        <div className="mx-auto max-w-3xl px-5">
          <Reveal>
            <Eyebrow>Legal</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-5 text-base leading-relaxed text-mutedblue">
              {SITE.legalName} (ABN {SITE.abn}) respects your privacy. This policy explains how we
              collect, use and protect personal information submitted through this website, including
              quote, contact, warranty and rebate enquiries.
            </p>
          </Reveal>
          <div className="mt-12 space-y-10">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.03}>
                <section>
                  <h2 className="font-display text-lg font-bold text-navy">{s.title}</h2>
                  {s.body.map((p, j) => (
                    <p key={j} className="mt-3 text-sm leading-relaxed text-mutedblue">
                      {p}
                    </p>
                  ))}
                </section>
              </Reveal>
            ))}
            <Reveal>
              <section className="rounded-[16px] border border-cardline bg-offwhite p-6">
                <h2 className="font-display text-lg font-bold text-navy">Contact us</h2>
                <p className="mt-3 text-sm leading-relaxed text-mutedblue">
                  {SITE.legalName}
                  <br />
                  {SITE.address1}, {SITE.address2}
                  <br />
                  Phone:{" "}
                  <a href={SITE.phoneHref} className="text-brand-blue hover:underline" data-testid="privacy-phone-link">
                    {SITE.phone}
                  </a>
                  <br />
                  Email:{" "}
                  <a href={SITE.emailHref} className="break-all text-brand-blue hover:underline" data-testid="privacy-email-link">
                    {SITE.email}
                  </a>
                </p>
              </section>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
